import { React, useEffect, useState, Suspense } from "react";
import ReactPlayer from "react-player";
import {
  Container,
  Row,
  Col,
  Accordion,
  Alert,
  Spinner,
} from "react-bootstrap";

import { useNavigate, useLocation } from "react-router-dom";

import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";

import { getRecommendationsByAssessmentId, getAllTaskRecommendations } from "../services/APIClient";

import "../components/Recommendations.css";
import ReturnToSencoResults from "../components/ReturnToSencoResults";

function Recommendations({ gradeLevel = "Placeholder" }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // Get assessmentId from router state
  const assessmentId = location.state?.assessmentId;

  useEffect(() => {
    window.HubSpotConversations?.widget?.refresh();
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    fetchRecommendations();
  }, [assessmentId]);

  const fetchRecommendations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (assessmentId) {
        // Fetch recommendations for specific assessment
        const data = await getRecommendationsByAssessmentId(assessmentId);
        setTasks(Array.isArray(data) ? data : []);
      } else {
        // Fallback: fetch all recommendations (old hardcoded data)
        const rec = getAllTaskRecommendations();
        setTasks(rec.sort((a, b) => a.id - b.id));
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      setError("Failed to load recommendations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={"Recommendations"}
        isLoggedIn={true}
        cloud={true}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          <Row className="inline-block align-items-center push-down mt-4">
            <Col className="col-auto">
              <Suspense fallback={<div>Loading Navigation Button...</div>}>
                <ReturnToSencoResults />
              </Suspense>
            </Col>
            <Col className="flex-grow-1 text-center" style={{ margin: "10px" }}>
              <h2 className="page-description-header-results">
                Recommendations
              </h2>
            </Col>
            <Col className="col-auto d-flex justify-content-end"></Col>
            <Col className="col-auto"></Col>
          </Row>
          <div className="class-overview-container">
            <Container className="container-shape">
              <Row className="page-description-bar">
                <Col className="col-8">
                  <h2 className="page-description-header">Recommendations</h2>
                  <h6>
                    Read the General Considerations and then access the PDF
                    guides for each task.
                  </h6>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col className="col-12">
                  <p>
                    <b>General Considerations</b>
                  </p>
                  <p>
                    Explicit, systematic and intensive teaching is required
                    particularly for children who are scoring just above, at or
                    below the 20th percentile.
                  </p>
                  <br />
                  <p>
                    <b>Explicit teaching</b> - Teaching is direct, intentional,
                    interactive and multisensory
                  </p>
                  <p>
                    <b>Systematic teaching</b> - Content to be taught is
                    introduced in an ordered, logical and sequential manner
                  </p>
                  <p>
                    <b>Intensive teaching</b> - Repetition and reinforcement of
                    content occurs very regularly
                  </p>
                  <br />
                  <p>
                    <b>Screening Considerations</b> - To find out more about why
                    it is important to screen for early literacy skills, see our
                    guidance document below:
                  </p>
                  <table className="recommendationsBody">
                    <tbody>
                      <tr>
                        <td>
                          <img
                            src="/assets/imgs/pdf.jpg"
                            alt="View recommendation"
                          />
                          <a
                            href="./docs/ALPACA_Guide_to_Screening.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Guide to Screening Early Literacy Skills
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <br />
                  <p>
                    Download the PDFs below to see specific recommendations for
                    each task
                  </p>
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              )}

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-3">Loading recommendations...</p>
                </div>
              ) : (
                <Row>
                  <Accordion alwaysOpen={true}>
                    {tasks?.map((t, index) => (
                      <Accordion.Item
                        key={t.taskid || index}
                        eventKey={index.toString()}
                        style={{ border: "white" }}
                      >
                        <Accordion.Header>
                          {assessmentId 
                            ? `${t.task_name}` 
                            : `Task ${t.id} - ${t.name}`
                          }
                        </Accordion.Header>
                        <Accordion.Body>
                          {/* Description */}
                          {t.description && (
                            <Row>
                              <div className="description">{t.description}</div>
                            </Row>
                          )}

                          {/* Video (only for old format) */}
                          {t.video && (
                            <Row>
                              <div className="video">
                                <ReactPlayer controls url={t.video} />
                              </div>
                            </Row>
                          )}

                          {/* New format: recommendations array */}
                          {assessmentId && t.recommendations && t.recommendations.length > 0 && (
                            <table className="files">
                              <thead>
                                <tr>
                                  <th>Recommendation</th>
                                  <th>PDF Link</th>
                                </tr>
                              </thead>
                              <tbody className="recommendationsBody">
                                {t.recommendations.map((rec, recIndex) => (
                                  <tr key={rec.id || recIndex}>
                                    <td>
                                      <strong>{rec.name}</strong>
                                      {rec.description && (
                                        <div className="text-muted small">
                                          {rec.description}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <img
                                        src="/assets/imgs/pdf.jpg"
                                        alt="View recommendation"
                                        style={{ width: "30px", marginRight: "10px" }}
                                      />
                                      <a
                                        href={rec.url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View PDF
                                      </a>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                          {/* Old format: files object */}
                          {!assessmentId && t.files && (
                            <table className="files">
                              <thead>
                                <tr>
                                  {t.files.all && <td></td>}
                                  {t.files.low && (
                                    <td>
                                      <h4>Low Results</h4>
                                      <div className="interval low">
                                        0 to 25%
                                      </div>
                                    </td>
                                  )}
                                  {t.files.medium && (
                                    <td>
                                      <h4>Medium Results</h4>
                                      <div className="interval medium">
                                        25 to 75%
                                      </div>
                                    </td>
                                  )}
                                  {t.files.high && (
                                    <td>
                                      <h4>High Results</h4>
                                      <div className="interval high">
                                        25 to 100%
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="recommendationsBody">
                                <tr>
                                  {t.files.all && (
                                    <td>
                                      <img
                                        src="/assets/imgs/pdf.jpg"
                                        alt="View recommendation"
                                      />
                                      <a
                                        href={t.files.all}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {t.name} Recommendations PDF
                                      </a>
                                    </td>
                                  )}
                                  {t.files.low && (
                                    <td>
                                      <img
                                        src="/assets/imgs/pdf.jpg"
                                        alt="View recommendation"
                                      />
                                      <br />
                                      <a
                                        href={t.files.low}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View Recommendation (PDF)
                                      </a>
                                    </td>
                                  )}
                                  {t.files.medium && (
                                    <td>
                                      <img
                                        src="/assets/imgs/pdf.jpg"
                                        alt="View recommendation"
                                      />
                                      <br />
                                      <a
                                        href={t.files.medium}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View Recommendation (PDF)
                                      </a>
                                    </td>
                                  )}
                                  {t.files.high && (
                                    <td>
                                      <img
                                        src="/assets/imgs/pdf.jpg"
                                        alt="View recommendation"
                                      />
                                      <br />
                                      <a
                                        href={t.files.high}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        View Recommendation (PDF)
                                      </a>
                                    </td>
                                  )}
                                </tr>
                              </tbody>
                            </table>
                          )}

                          {/* No recommendations available */}
                          {assessmentId && (!t.recommendations || t.recommendations.length === 0) && (
                            <p className="text-muted">No recommendations available for this task.</p>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>

                  {tasks.length === 0 && !isLoading && (
                    <Alert variant="info" className="mt-3">
                      No recommendations found for this assessment.
                    </Alert>
                  )}
                </Row>
              )}
            </Container>
          </div>
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default Recommendations;